import type { ReactNode, Ref } from 'react';
import React from 'react';
import { mediaQueries, styled, theme } from '@components-shared/src/stitches';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { LOADING_STYLES } from '@components/main-components/skeleton/Skeleton';
import { MediaQuery } from '@context/mediaQuery';
import type { CurrentListeningParty } from '@hooks/usePlayButton';
import { useChartCardContext } from './Context';
import type { CHANGE_TYPES } from './Rank';
import { ChartCardRank } from './Rank';

const ASSUMED_IMAGE_SIZE = 96;
const blurPlaceholder = blurImage(ASSUMED_IMAGE_SIZE, ASSUMED_IMAGE_SIZE);

// We've gone back and forth on the amount of sizes and the sizes themselves
// Right now tablet and desktop are the same size, but we might want to change that in the future
export const CHART_CARD_COVER_IMAGE_SIZES = {
  mobile: 92,
  tablet: 152,
  desktop: 176,
  minimalMobile: 68,
  minimalTablet: 96,
} as const;

const Image = ({
  src,
  unoptimized,
  preloadImage = false,
}: {
  src: string;
  /**
   * Set to true to load desktop size
   */
  desktop?: boolean;
  /**
   * MAKE SURE YOU KNOW WHAT YOU ARE DOING.
   *
   * This was added so that we can render the image in storybook preview.
   * Only scenario you would want to use this is if you are trying to render GIFs
   *
   * But if you are thinking to render GIFs would HIGHLY recommend consulting with the team first.
   */
  unoptimized: boolean;
  /**
   * Set to true to load image with priority
   * @default false
   * @see https://nextjs.org/docs/basic-features/image-optimization#lazy-loading
   */
  preloadImage: boolean;
}) => {
  return (
    <NextFutureImage
      priority={preloadImage}
      src={src}
      fill
      alt="Chart card cover"
      unoptimized={unoptimized}
      // an exception to our size flag since this is an optimization of the images we fetch rather than visual looks
      sizes={`${CHART_CARD_COVER_IMAGE_SIZES['mobile']}px, ${CHART_CARD_COVER_IMAGE_SIZES['minimalMobile']}px, ${mediaQueries['tablet']} ${CHART_CARD_COVER_IMAGE_SIZES.mobile}px, ${CHART_CARD_COVER_IMAGE_SIZES.minimalTablet}px`}
      placeholder="blur"
      blurDataURL={blurPlaceholder}
    />
  );
};

export function ChartCardCover({
  onPlayButtonClick,
  isBufferingChart,
  coverLink,
  image,
  disablePlay,
  rank,
  change,
  playButtonPrefetchRef,
  currentListeningParty,
}: {
  /**
   * Callback to be called when the play button is clicked
   */
  onPlayButtonClick: (e: React.MouseEvent) => void;
  /**
   * loading indicator for the play button
   */
  isBufferingChart?: boolean;
  /**
   * Link to the cover image for navigation
   */
  coverLink?: string | undefined;
  /**
   * To show play button in disabled state
   */
  disablePlay?: boolean;
  /**
   * Image Component to render. React node so that we can add onClick events etc.
   */
  image: ReactNode;
  /**
   * Current rank of the chart
   */
  rank?: number;
  /**
   * Trending indicator of the chart
   */
  change?: keyof typeof CHANGE_TYPES;

  playButtonPrefetchRef: Ref<HTMLButtonElement>;

  currentListeningParty: CurrentListeningParty;
}) {
  const { isPlaying, isLoadingSummary, minimal } = useChartCardContext();
  const { tablet } = MediaQuery.useContainer();
  const size = tablet ? 'tablet' : 'mobile';

  if (isLoadingSummary) {
    return <Container loading={isLoadingSummary} minimal={minimal} />;
  }

  const playButtonSize = minimal ? 'xxl' : size === 'mobile' ? 'xxl' : 'l';
  return (
    <Container minimal={minimal}>
      <CoverImageContainer>
        {coverLink ? (
          <AccessibleLink absoluteUri={coverLink} type="external">
            {image}
          </AccessibleLink>
        ) : (
          image
        )}
      </CoverImageContainer>
      {rank && change && <ChartCardRank rank={rank} change={change} />}
      <PlayButtonContainer
        className="md:group-hover:opacity-100"
        playing={isPlaying}
        size={size}
        minimal={minimal}
      >
        <PlayButton
          color={size === 'mobile' || minimal ? 'transparent' : 'white'}
          playing={isPlaying}
          onClick={onPlayButtonClick}
          size={playButtonSize}
          loading={isBufferingChart}
          disabled={disablePlay}
          ref={playButtonPrefetchRef}
          pseudo
          currentListeningParty={currentListeningParty}
        />
      </PlayButtonContainer>
    </Container>
  );
}

/**
 * To render an image in chart card.
 */
ChartCardCover.Image = Image;

const Container = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  pointerEvents: 'all',
  overflow: 'hidden',
  borderRadius: '$medium',
  // https://github.com/google/model-viewer/issues/662
  // Fixes the issue where border radius was not applying in Safari
  transform: 'translateZ(0)',
  flexShrink: 0,
  width: CHART_CARD_COVER_IMAGE_SIZES.mobile,
  height: CHART_CARD_COVER_IMAGE_SIZES.mobile,

  compoundVariants: [
    {
      minimal: true,
      loading: true,
      css: {
        width: CHART_CARD_COVER_IMAGE_SIZES.minimalMobile,
        height: CHART_CARD_COVER_IMAGE_SIZES.minimalMobile,

        '@tablet': {
          width: CHART_CARD_COVER_IMAGE_SIZES.minimalTablet,
          height: CHART_CARD_COVER_IMAGE_SIZES.minimalTablet,
        },
      },
    },
  ],

  variants: {
    minimal: {
      true: {
        width: CHART_CARD_COVER_IMAGE_SIZES.minimalMobile,
        height: CHART_CARD_COVER_IMAGE_SIZES.minimalMobile,

        '@tablet': {
          width: CHART_CARD_COVER_IMAGE_SIZES.minimalTablet,
          height: CHART_CARD_COVER_IMAGE_SIZES.minimalTablet,
        },
      },
      false: {
        width: CHART_CARD_COVER_IMAGE_SIZES.mobile,
        height: CHART_CARD_COVER_IMAGE_SIZES.mobile,

        '@tablet': {
          width: CHART_CARD_COVER_IMAGE_SIZES.tablet,
          height: CHART_CARD_COVER_IMAGE_SIZES.tablet,
        },

        '@desktop': {
          width: CHART_CARD_COVER_IMAGE_SIZES.desktop,
          height: CHART_CARD_COVER_IMAGE_SIZES.desktop,
        },
      },
    },
    loading: {
      true: {
        ...LOADING_STYLES,

        '@tablet': {
          width: CHART_CARD_COVER_IMAGE_SIZES.tablet,
          height: CHART_CARD_COVER_IMAGE_SIZES.tablet,

          '@desktop': {
            width: CHART_CARD_COVER_IMAGE_SIZES.desktop,
            height: CHART_CARD_COVER_IMAGE_SIZES.desktop,
          },
        },
      },
    },
  },
});

export const CoverImageContainer = styled('div', {
  overflow: 'hidden',
  width: '100%',
  height: '100%',

  img: {
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
  },
});

export const PlayButtonContainer = styled('div', {
  transition: `opacity ${theme.transitions.slow}`,
  opacity: 0,
  position: 'absolute',
  cursor: 'pointer',

  compoundVariants: [
    {
      size: 'tablet',
      minimal: false,
      css: {
        bottom: 12,
        right: 12,
      },
    },
  ],

  variants: {
    playing: {
      true: {
        opacity: 1,
      },
    },
    size: {
      tablet: {},
      mobile: {},
    },
    minimal: {
      true: {},
      false: {
        position: 'absolute',
      },
    },
  },
});
